import React from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, Cake, ArrowRight } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";

const IMG_POPS = "https://images.unsplash.com/photo-1555526148-5cd740b44241?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxjYWtlJTIwcG9wcyUyMGRlc3NlcnR8ZW58MHx8fHwxNzc3MDQ4NTMyfDA&ixlib=rb-4.1.0&q=85";
const IMG_BOX = "https://images.pexels.com/photos/10171574/pexels-photo-10171574.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  return (
    <div className="bg-background" data-testid="page-about">
      <section className="relative section">
        <div className="mx-auto max-w-5xl text-center">
          <span className="chip"><Heart className="h-3 w-3" /> The Story</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-sakura-ink">Sweet Treats, Made with Heart</h1>
          <SakuraDivider />
        </div>
      </section>

      <section className="section pt-0">
        <div className="mx-auto max-w-6xl grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative">
            <div className="rounded-[32px] overflow-hidden shadow-sakura border border-sakura-200">
              <img src={IMG_POPS} alt="Handcrafted cake pops" className="w-full h-[460px] object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -right-6 bg-white border border-sakura-200 rounded-2xl p-4 shadow-soft max-w-[220px]">
              <div className="flex items-center gap-2 text-sakura-500 text-xs font-bold uppercase tracking-wider"><Sparkles className="h-3 w-3" /> Made with love</div>
              <div className="mt-1 font-serif text-lg text-sakura-ink leading-snug">Every order is hand-decorated.</div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="first-letter:font-serif first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-sakura-500 text-sakura-ink text-lg leading-relaxed">
              Hi, I'm Kaitlyn, the creator behind Sakura Treats. I love making custom treats that feel special, creative, and beautiful. What started as a love for baking, decorating, and making celebrations extra sweet has grown into a small business focused on handcrafted desserts with a whimsical touch.
            </p>
            <p className="mt-5 text-sakura-soft text-base leading-relaxed">
              Every order is made with care, whether it's a dozen cake pops for a birthday, themed treats for a party, or a sweet gift box for someone you love.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/custom-orders" className="btn-primary" data-testid="about-cta-order"><Sparkles className="h-4 w-4" /> Start a Custom Order</Link>
              <Link to="/gallery" className="btn-secondary" data-testid="about-cta-gallery">See Past Work <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-sakura-100/50" data-testid="about-values">
        <div className="mx-auto max-w-6xl grid md:grid-cols-3 gap-6">
          {[
            { icon: Cake, title: "Hand-Decorated", desc: "Each pop, pretzel, and piece is decorated by hand, one at a time." },
            { icon: Heart, title: "Made to Your Moment", desc: "Tell me your colors, theme, vibe — and I'll create something that feels like you." },
            { icon: Sparkles, title: "Small Batch Quality", desc: "A limited number of orders each week so every treat is its best self." },
          ].map((v) => (
            <div key={v.title} className="card-soft p-7">
              <span className="h-11 w-11 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><v.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-serif text-xl text-sakura-ink">{v.title}</h3>
              <p className="mt-2 text-sakura-soft text-sm">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="mx-auto max-w-5xl card-soft p-10 md:p-14 text-center relative overflow-hidden">
          <img src={IMG_BOX} alt="Dessert box" className="absolute inset-0 w-full h-full object-cover opacity-10" />
          <div className="relative">
            <h2 className="font-serif text-3xl sm:text-4xl text-sakura-ink">Let's make your celebration sweeter</h2>
            <p className="mt-3 text-sakura-soft max-w-xl mx-auto">Reach out with your date, theme, and ideas — I'll take it from there.</p>
            <Link to="/custom-orders" className="btn-primary mt-6" data-testid="about-bottom-cta"><Sparkles className="h-4 w-4" /> Inquire Now</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
